import * as React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState, NotFoundState, ConfirmationModal } from "@bi-studio/shared/components";
import { deleteTabGroup, getFolderForItem } from "@bi-studio/shared/apis";
import { TabGroupView } from "@/components/TabGroupView";
import { PageContainer, PageActions, TabGroupActionsButton, ItemDetailModal } from "@/components";
import { LoadingOverlay } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { 
    fetchTabGroupDetails, 
    selectCurrentTabGroup, 
    selectTabGroupLoading, 
    selectTabGroupError,
    clearCurrentTabGroup
} from "@/store/slices/tabGroupSlice";

export default function TabGroupDetailPage() {
    const { id: tabGroupId } = useParams<{ id: string }>();
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const isEmbed = searchParams.get("embed") === "true";

    const tabGroup = useAppSelector(selectCurrentTabGroup);
    const isLoading = useAppSelector(selectTabGroupLoading);
    const error = useAppSelector(selectTabGroupError);

    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    React.useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            handleLogout();
        }
    }, [isAuthenticated, authLoading, handleLogout]);

    React.useEffect(() => {
        const currentToken = token;
        if (currentToken && tabGroupId) {
            dispatch(fetchTabGroupDetails({ token: currentToken!, id: tabGroupId! }));
        }

        const handleUpdated = (e: any) => {
            if (e.detail?.tabGroupId === tabGroupId && currentToken) {
                dispatch(fetchTabGroupDetails({ token: currentToken!, id: tabGroupId! }));
            }
        };

        window.addEventListener("tab-group-updated", handleUpdated as any);

        return () => {
            dispatch(clearCurrentTabGroup());
            window.removeEventListener("tab-group-updated", handleUpdated as any);
        };
    }, [dispatch, token, tabGroupId]);

    const handleBack = React.useCallback(async () => {
        if (!token || !tabGroupId) {
            navigate(-1);
            return;
        }

        try {
            const folderInfo = await getFolderForItem(token, "tab_group", tabGroupId);
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            console.error("Failed to get folder for tab group:", err);
            navigate(-1);
        }
    }, [token, tabGroupId, navigate]);

    const handleDelete = async () => {
        if (!token || !tabGroupId) return;

        try {
            setIsDeleting(true);
            await deleteTabGroup(token, tabGroupId);
            navigate('/folders');
        } catch (err) {
            console.error("Failed to delete tab group", err);
            setIsDeleteModalOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const isReady = !!tabGroup;
    const isPageLoading = isLoading || authLoading;

    // 1. Loading State
    if (!isReady && isPageLoading) {
        return (
            <AppLayout onLogout={handleLogout}>
                <LoadingScreen message="Loading tab group..." />
            </AppLayout>
        );
    }

    // 2. Error State
    if (error) {
        return (
            <AppLayout onLogout={handleLogout}>
                <ErrorState 
                    title="Error Loading Tab Group"
                    message={error}
                    onAction={() => window.location.reload()}
                    actionLabel="Retry"
                />
            </AppLayout>
        );
    }

    // 3. Not Found State
    if (!isReady && !isPageLoading) {
        const fallback = (
            <NotFoundState 
                title="Tab Group Not Found"
                message="The tab group you are looking for does not exist or you don't have permission to view it."
                onAction={() => navigate("/folders")}
                actionLabel="Back to Folders"
            />
        );
        return isEmbed ? fallback : <AppLayout onLogout={handleLogout}>{fallback}</AppLayout>;
    }

    // 4. Main Content
    const content = (
        <PageContainer>
            <LoadingOverlay visible={isLoading && isReady} />
            <PageActions title={tabGroup!.name} onBack={handleBack}>
                {!isEmbed && (
                    <TabGroupActionsButton 
                        setIsDetailsOpen={setIsDetailsOpen}
                        setIsDeleteModalOpen={setIsDeleteModalOpen}
                    />
                )}
            </PageActions>

            <TabGroupView tabGroup={tabGroup!} />

            <ItemDetailModal 
                opened={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                itemType="tab_group"
                itemId={tabGroupId!}
            />

            <ConfirmationModal
                opened={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Tab Group"
                confirmLabel="Delete"
                confirmColor="red"
                loading={isDeleting}
            >
                {`Are you sure you want to delete "${tabGroup!.name}"? This action cannot be undone.`}
            </ConfirmationModal>
        </PageContainer>
    );

    return isEmbed ? content : <AppLayout onLogout={handleLogout}>{content}</AppLayout>;
}
